import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import moment from "moment";

/**
 * Unread-mail bell for the reviewer shell.
 *
 * Opening a row only opens the thread. The read call belongs to the popup --
 * sending a reply, or clicking Mark as Read -- so a thread the reviewer opened
 * and closed without acting on stays unread. Fetching the feed never marks
 * anything either, so a notification that merely arrived stays unread.
 *
 * Read state is held for the session only. It used to be mirrored in
 * localStorage, which let this browser mark a thread read while the server
 * still reported it unread - the badge then read 0 over a row the feed says
 * has unread messages. The unread endpoint is the authority; a reload shows
 * what it says.
 */
@Component({
  name: "notification-bell",
})
export default class NotificationBellComponent extends Vue {
  /** Mail threads to surface, newest first. Shape: PersonalMailModel. */
  @Prop({ default: () => [] }) mails!: any[];

  /** How many rows the panel shows before it scrolls. */
  @Prop({ default: 6 }) maxItems!: number;

  public isPanelOpen = false;
  public readIds: string[] = [];

  private legacyStorageKey = "cprs-notification-read-ids";

  created() {
    // Drop what earlier builds persisted, so a stale entry cannot keep
    // suppressing the badge for a thread the server still reports unread.
    try {
      localStorage.removeItem(this.legacyStorageKey);
    } catch (error) {
      // Nothing to clean up if the store is unavailable.
    }
  }

  mounted() {
    document.addEventListener("click", this.onDocumentClick);
  }

  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
  }

  @Watch("mails")
  onMailsChanged() {
    // Drop ids that no longer exist so the list cannot grow without bound.
    const liveIds = this.mails.map((mail: any) => this.idFor(mail));
    this.readIds = this.readIds.filter((id: string) => liveIds.includes(id));
  }

  public get visibleMails(): any[] {
    return this.mails.slice(0, this.maxItems);
  }

  /**
   * Counts messages, not rows: one thread can hold several unread replies, and
   * the sum matches the totalUnreadCount the endpoint reports.
   */
  public get unreadCount(): number {
    return this.mails
      .filter((mail: any) => !this.isRead(mail))
      .reduce((total: number, mail: any) => {
        return total + (Number(mail.unreadCount) || 0);
      }, 0);
  }

  /** Badge caps at 9+ so a long queue cannot stretch the pill. */
  public get badgeLabel(): string {
    return this.unreadCount > 9 ? "9+" : `${this.unreadCount}`;
  }

  public togglePanel(): void {
    this.isPanelOpen = !this.isPanelOpen;
  }

  public closePanel(): void {
    this.isPanelOpen = false;
  }

  public isRead(mail: any): boolean {
    return this.readIds.includes(this.idFor(mail));
  }

  public openMail(mail: any): void {
    this.closePanel();
    this.$emit("open-mail", mail);
  }

  /** "Marcus Reed replied to "Renewal CEU"" — the sender is who last wrote. */
  public titleFor(mail: any): string {
    const name = `${mail.latestSenderFirstName || ""} ${
      mail.latestSenderLastName || ""
    }`
      .replace(/\s+/g, " ")
      .trim();
    const sender = name || mail.latestSenderEmail || "Someone";
    const subject = mail.latestSubject || "your application";
    return `${sender} replied to "${subject}"`;
  }

  /**
   * The unread summary carries no message body - only per-thread counts - so
   * the second line reports what is waiting rather than quoting it.
   */
  public previewFor(mail: any): string {
    const count = Number(mail.unreadCount) || 0;
    if (count > 1) {
      return `${count} unread messages`;
    }
    return "1 unread message";
  }

  /**
   * Compact relative time: "10 min ago", not moment's "10 minutes ago", and
   * "1 hour ago" rather than "an hour ago". Anything past a week reads as a
   * date, where "9 days ago" stops being useful.
   */
  public timeAgo(mail: any): string {
    const raw = mail.latestReceivedDate || "";
    if (!raw) {
      return "";
    }
    // Timestamps are UTC with no zone marker, so they are read as UTC and
    // shown local - parsed plain, every notification dates an offset early.
    const stamp = moment
      .utc(raw, [
        moment.ISO_8601,
        "YYYY-MM-DD HH:mm:ss.SSSSSS",
        "YYYY-MM-DD HH:mm:ss.SSS",
        "YYYY-MM-DD HH:mm:ss",
      ])
      .local();
    if (!stamp.isValid()) {
      return "";
    }
    const minutes = moment().diff(stamp, "minutes");
    if (minutes < 1) {
      return "just now";
    }
    if (minutes < 60) {
      return `${minutes} min ago`;
    }
    const hours = moment().diff(stamp, "hours");
    if (hours < 24) {
      return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    }
    const days = moment().diff(stamp, "days");
    if (days < 7) {
      return `${days} day${days === 1 ? "" : "s"} ago`;
    }
    return stamp.format("MMM D, YYYY");
  }

  /**
   * Which key this mail is addressed by. The history and read routes accept
   * applicationId, userId or email, and only the record says which one it has.
   */
  private scopeFor(mail: any): string {
    if (mail.applicationId) {
      return `applicationId=${mail.applicationId}`;
    }
    if (mail.userId) {
      return `userId=${mail.userId}`;
    }
    if (mail.emailId) {
      return `email=${encodeURIComponent(mail.emailId)}`;
    }
    return "";
  }

  /** A row is a thread, not a mail, and the scope is what identifies it. */
  private idFor(mail: any): string {
    return this.scopeFor(mail);
  }

  private onDocumentClick(event: Event): void {
    if (!this.isPanelOpen) {
      return;
    }
    const root = this.$refs.bellRoot as HTMLElement;
    if (root && !root.contains(event.target as Node)) {
      this.closePanel();
    }
  }
}
